/**
 * Рукопожатие с iframe-виджетом брокера (пополнение / вывод).
 *
 * Протокол: виджет шлёт `<prefix>:ready` → мы выпускаем на бэкенде одноразовую
 * сессию и отдаём её `<prefix>:init {session}` — строго в окно iframe и строго на
 * origin виджета. `reauth-required` — сессия истекла или погашена: выпускаем новую.
 * Остальное — `credited`, `close`, `resize`, `error`.
 *
 * Модуль без DOM и React: всю логику безопасности можно проверить юнит-тестом.
 * Главные правила:
 *  - сообщение принимается, только если пришло ИЗ окна нашего iframe И с origin виджета;
 *  - сессия уходит только на origin виджета, никогда на `*`;
 *  - суммам из событий не верим — `credited` лишь повод сверить платежи на сервере.
 */

export interface WidgetSessionInfo {
  session: string;
  embedOrigin: string;
  eventPrefix: string;
}

export interface BridgeMessage {
  origin: string;
  source: unknown;
  data: unknown;
}

export interface WalletBridgeOptions {
  /** Origin виджета и префикс событий — с сервера, вместе с первой сессией. */
  embedOrigin: string;
  eventPrefix: string;
  /** Окно iframe на момент события (iframe может пересоздаться). */
  frameWindow: () => { postMessage(message: unknown, targetOrigin: string): void } | null;
  /** Выпустить новую сессию на бэкенде. `renew` — повторный выпуск по `reauth-required`. */
  mintSession: (renew: boolean) => Promise<WidgetSessionInfo>;
  onCredited?: () => void;
  onClose?: () => void;
  onResize?: (height: number) => void;
  onError?: (message: string) => void;
}

export const MIN_WIDGET_HEIGHT = 420;
export const MAX_WIDGET_HEIGHT = 900;

export function clampWidgetHeight(height: number): number {
  return Math.min(MAX_WIDGET_HEIGHT, Math.max(MIN_WIDGET_HEIGHT, Math.round(height)));
}

export interface WalletBridge {
  /** Отдать в обработчик `window.message`. true — сообщение наше и обработано. */
  handle(message: BridgeMessage): boolean;
  /** Сессия, полученная вместе с адресом iframe: отдадим её на первый `ready`, не выпуская вторую. */
  primeSession(session: string): void;
}

export function createWalletBridge(options: WalletBridgeOptions): WalletBridge {
  let primed: string | null = null;
  let initialized = false;
  let minting = false;

  async function sendSession(renew: boolean): Promise<void> {
    if (minting) return;
    minting = true;
    try {
      let session = primed;
      primed = null;
      if (!session) {
        const fresh = await options.mintSession(renew);
        // сервер сменил origin виджета — не наш случай: сессию в неизвестное окно не шлём
        if (fresh.embedOrigin !== options.embedOrigin) {
          options.onError?.('widget origin changed');
          return;
        }
        session = fresh.session;
      }
      const target = options.frameWindow();
      if (!target) return;
      target.postMessage({ type: `${options.eventPrefix}:init`, session }, options.embedOrigin);
      initialized = true;
    } catch (err) {
      options.onError?.(err instanceof Error ? err.message : 'session failed');
    } finally {
      minting = false;
    }
  }

  return {
    primeSession(session: string): void {
      primed = session;
    },

    handle(message: BridgeMessage): boolean {
      // чужая вкладка, расширение браузера, другой iframe — мимо
      if (message.origin !== options.embedOrigin) return false;
      const frame = options.frameWindow();
      if (!frame || message.source !== frame) return false;

      const data = message.data as { type?: unknown; height?: unknown; message?: unknown } | null;
      const type = typeof data?.type === 'string' ? data.type : '';
      const prefix = `${options.eventPrefix}:`;
      if (!type.startsWith(prefix)) return false;

      switch (type.slice(prefix.length)) {
        case 'ready':
          void sendSession(initialized);
          return true;
        case 'reauth-required':
          void sendSession(true);
          return true;
        case 'credited':
          options.onCredited?.();
          return true;
        case 'close':
          options.onClose?.();
          return true;
        case 'resize':
          if (typeof data?.height === 'number' && Number.isFinite(data.height)) {
            options.onResize?.(clampWidgetHeight(data.height));
          }
          return true;
        case 'error':
          options.onError?.(typeof data?.message === 'string' ? data.message : 'widget error');
          return true;
        default:
          return true;
      }
    },
  };
}
